import EnumReferralRewardType from "./EnumReferralRewardType";
import EnumReferralTrackingStatus from "./EnumReferralTrackingStatus";
import EnumReferralTrigger from "./EnumReferralTrigger";

function parseEnumValue<T extends Record<string, number | string>>(enumObject: T, value: any): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  // Если это уже число и оно валидно, проверим что оно есть в enum
  if (typeof value === "number" && !Number.isNaN(value)) {
    // Проверяем что это валидное значение enum
    const numericKeys = Object.keys(enumObject)
      .filter(key => !isNaN(Number(key)))
      .map(k => Number(k));
    if (numericKeys.includes(value)) {
      return value;
    }
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    
    // Пытаемся распарсить как число
    const asNumber = Number(trimmed);
    if (!Number.isNaN(asNumber) && /^-?\d+$/.test(trimmed)) {
      return asNumber;
    }

    // Точное совпадение ключа
    if (Object.prototype.hasOwnProperty.call(enumObject, trimmed)) {
      const enumValue = (enumObject as Record<string, number | string>)[trimmed];
      return typeof enumValue === "number" ? enumValue : null;
    }

    // Case-insensitive поиск по имени ключа
    const enumKey = Object.keys(enumObject).find(
      (key) => isNaN(Number(key)) && key.toLowerCase() === trimmed.toLowerCase()
    );
    if (enumKey) {
      const enumValue = (enumObject as Record<string, number | string>)[enumKey];
      return typeof enumValue === "number" ? enumValue : null;
    }
  }

  return null;
}

export function parseReferralTrigger(value: any): number | null {
  return parseEnumValue(EnumReferralTrigger, value);
}

export function parseReferralRewardType(value: any): number | null {
  return parseEnumValue(EnumReferralRewardType, value);
}

export function parseReferralTrackingStatus(value: any): number | null {
  if (typeof value === "string" && value.trim().toLowerCase() === "rewarded") {
    return EnumReferralTrackingStatus.Completed;
  }
  return parseEnumValue(EnumReferralTrackingStatus, value);
}

export const defaultReferralRuleUpsertTrigger = EnumReferralTrigger.InvitedPurchasedPremium;

export function isReferralRuleUpsertTriggerSupported(value: any): boolean {
  return parseReferralTrigger(value) !== null;
}

function toEnumApiValue<T extends Record<string, number | string>>(
  enumObject: T,
  value: any,
  parser: (raw: any) => number | null
): string | null {
  const parsedValue = parser(value);
  if (parsedValue === null) {
    return null;
  }

  const enumKey = enumObject[parsedValue];
  return typeof enumKey === "string" ? enumKey : null;
}

export function toReferralTriggerApiValue(value: any): string | null {
  return toEnumApiValue(EnumReferralTrigger, value, parseReferralTrigger);
}

export function toReferralRewardTypeApiValue(value: any): string | null {
  return toEnumApiValue(EnumReferralRewardType, value, parseReferralRewardType);
}

export function toReferralTrackingStatusApiValue(value: any): string | null {
  return toEnumApiValue(EnumReferralTrackingStatus, value, parseReferralTrackingStatus);
}

export interface ReferralRuleFieldMeta {
  usesThreshold: boolean;
  usesWindow: boolean;
  usesRewardDays: boolean;
}

const triggerFieldMeta: Record<number, Pick<ReferralRuleFieldMeta, "usesThreshold" | "usesWindow">> = {
  0: { usesThreshold: false, usesWindow: false },
  1: { usesThreshold: false, usesWindow: false },
  2: { usesThreshold: true, usesWindow: false },
  3: { usesThreshold: true, usesWindow: true },
  4: { usesThreshold: true, usesWindow: false },
};

const rewardFieldMeta: Record<number, Pick<ReferralRuleFieldMeta, "usesRewardDays">> = {
  0: { usesRewardDays: true },
  1: { usesRewardDays: false },
  2: { usesRewardDays: false },
  3: { usesRewardDays: false },
};

export function buildUpsertReferralRulePayload(request: {
  RuleId?: number | null;
  Trigger: any;
  Threshold: any;
  WindowDays?: any;
  RewardType: any;
  RewardDays?: any;
}) {
  const trigger = parseReferralTrigger(request.Trigger) ?? defaultReferralRuleUpsertTrigger;
  const rewardType = parseReferralRewardType(request.RewardType) ?? 0;
  const triggerApiValue = toReferralTriggerApiValue(trigger);
  const rewardTypeApiValue = toReferralRewardTypeApiValue(rewardType);
  const triggerMeta = triggerFieldMeta[trigger];
  const rewardMeta = rewardFieldMeta[rewardType];

  // Обработка threshold - если trigger его не требует, используем 1
  let threshold = 1;
  if (triggerMeta && triggerMeta.usesThreshold) {
    const thresholdValue = Number(request.Threshold);
    threshold = Number.isNaN(thresholdValue) || thresholdValue < 1 ? 1 : thresholdValue;
  }

  const payload: {
    ruleId?: number;
    trigger: string;
    threshold: number;
    windowDays: number | null;
    rewardType: string;
    rewardDays: number | null;
  } = {
    trigger: triggerApiValue || EnumReferralTrigger[0],
    threshold,
    rewardType: rewardTypeApiValue || EnumReferralRewardType[0],
    windowDays: null,
    rewardDays: null,
  };

  if (typeof request.RuleId === "number") {
    payload.ruleId = request.RuleId;
  }

  if (
    triggerMeta &&
    triggerMeta.usesWindow &&
    request.WindowDays !== null &&
    request.WindowDays !== undefined &&
    request.WindowDays !== ""
  ) {
    const windowValue = Number(request.WindowDays);
    if (!Number.isNaN(windowValue) && windowValue > 0) {
      payload.windowDays = windowValue;
    }
  }

  if (rewardMeta && rewardMeta.usesRewardDays) {
    const rewardValue = Number(request.RewardDays);
    payload.rewardDays = Number.isNaN(rewardValue) || rewardValue < 1 ? 1 : rewardValue;
  }

  return payload;
}
